
import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface ScrollToBottomButtonProps {
  isVisible: boolean;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor';
}

export const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({ 
  isVisible, 
  onClick, 
  className,
  style,
  topic
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  // Handle button click with logging
  const handleClick = (e: React.MouseEvent) => {
    console.log('Scroll to bottom button clicked');
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={cn(
            // Absolute positioning - position will be controlled via className prop
            "absolute z-30",
            // Mobile-specific positioning adjustments
            isMobile && "touch-optimized",
            className
          )}
          style={{
            // Enhanced positioning with safe area support
            ...style,
            // Ensure proper z-index above content but below modals
            zIndex: 30
          }}
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ 
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleClick}
                  size={isMobile ? "sm" : "default"}
                  variant="secondary"
                  className={cn(
                    // Responsive sizing
                    isMobile ? "h-10 w-10 p-0" : "h-12 w-12 p-0",
                    "rounded-full",
                    "bg-background/95 hover:bg-background border border-border/50",
                    "shadow-lg backdrop-blur-sm transition-all duration-200",
                    "hover:shadow-xl hover:scale-105",
                    // Enhanced mobile touch target
                    isMobile && "min-h-[44px] min-w-[44px]"
                  )}
                  aria-label={t('ai_assistant.scroll_to_bottom')}
                  style={{
                    // Ensure button stays above content with backdrop blur
                    backdropFilter: 'blur(12px)',
                    // Smooth transform transitions
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <ChevronDown className={cn(
                    isMobile ? "h-4 w-4" : "h-5 w-5",
                    "transition-transform duration-200"
                  )} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-50">
                <p>{t('ai_assistant.scroll_to_bottom')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
